import Cookies from 'js-cookie'
import { UAParser } from 'ua-parser-js'
import { Constants, SdkConfig } from "../globals"
import { LocalStorageUtils } from "../utils";
import CookieUtils from "../utils/CookieUtils.js";

export const SIGNATURE = Symbol.for('cdp.sdk.Client')

class Client {
  constructor() {
    this[SIGNATURE] = true
    this.ensureUserKey()
    this.ensureSessionKey()
    this.ensureCampaign()
    this.uaParser = new UAParser()
    this.environment = {
      browser: this.uaParser.getResult().browser,
      device: {
        name: this.uaParser.getResult().device.model,
        type: this.getDeviceType()
      },
      os: this.uaParser.getResult().os,
      referrer: document.referrer,
      timezone: new window.Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  }

  ensureCampaign() {
    const url = new URL(location.href)
    if (url.searchParams.has('utmCampaignId')) {
      try {
        const campaignId = url.searchParams.get('utmCampaignId')
        const campaignInflow = {
          campaignId: campaignId,
          createdAt: Date.now()
        }
        LocalStorageUtils.getArrayValueAsJsonThenReplace(Constants.STORAGE_KEY_CAMPAIGN_INFLOW_LIST, 30, campaignInflowList => {
          return [
            ...campaignInflowList.filter(campaignInflow => campaignInflow.campaignId !== campaignId),
            campaignInflow
          ]
        })
        const cookie = Cookies.get(Constants.COOKIE_NAME_CAMPAIGN_EXPOSURE_LIST)
        const todayExposureCampaignIdList = (cookie?.split('|') || []).filter(Boolean).map(value => value.trim())
        todayExposureCampaignIdList.push(campaignId)
        Cookies.set(Constants.COOKIE_NAME_CAMPAIGN_EXPOSURE_LIST, [...new Set(todayExposureCampaignIdList)].join('|'), {
          expires: CookieUtils.getExpiryMidnight(),
        })
      } catch (e) {
        console.error(e)
      }
    }
  }

  getDeviceType() {
    const { type } = this.uaParser.getResult().device
    if (type === 'mobile') {
      return 'Phone'
    }
    if (type === 'tablet') {
      return 'Tablet'
    }
    return 'Desktop'
  }

  async ensureUserKey() {
    const clientId = Cookies.get(Constants.COOKIE_NAME_CLIENT_ID)
    if (clientId) {
      this.set('clientId', clientId)
    } else {
      const generatedId = crypto.randomUUID()
      Cookies.set(Constants.COOKIE_NAME_CLIENT_ID, generatedId, {
        expires: 365,
      })
      this.set('clientId', generatedId)
    }
  }

  async ensureSessionKey() {
    const sessionId = Cookies.get(Constants.COOKIE_NAME_SESSION_ID)
    if (sessionId) {
      this.set('sessionId', sessionId)
    } else {
      const generatedId = crypto.randomUUID()
      this.set('sessionId', generatedId)
    }
    Cookies.set(Constants.COOKIE_NAME_SESSION_ID, this.get('sessionId'), {
      expires: new Date(Date.now() + (1000 * 60 * 30)),
    })
  }

  remove(key) {
    delete this[key]
    if (SdkConfig.isDebugMode()) {
      console.debug('###DEBUG >> Removed Client Attribute --> ', key)
    }
  }

  set(key, value) {
    this[key] = value
    if (SdkConfig.isDebugMode()) {
      console.debug('###DEBUG >> Set Client Attribute --> ', key, ' = ', value)
    }
  }

  add(key, value) {
    this[key] = [
      ...this[key],
      value
    ]
    if (SdkConfig.isDebugMode()) {
      console.debug('###DEBUG >> Add Client Attribute --> ', key, ' = ', value)
    }
  }

  get(key) {
    return this[key]
  }

  contains(key) {
    return Object.prototype.hasOwnProperty.call(this, key) && this[key] !== undefined
  }

  static fromJson(json) {
    const data = typeof json === 'string' ? JSON.parse(json) : json && typeof json === 'object'  ? json  : {}
    const client = new Client()
    for (const [key, value] of Object.entries(data)) {
      if (value === undefined) {
        continue
      }
      if (key === 'environment' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
        client.set('environment', {
          ...client.environment,
          ...value,
        })
      } else {
        client.set(key, value)
      }
    }
    return client
  }

  static isClient(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false
    }
    if (value instanceof Client) {
      return true
    }
    return value[SIGNATURE]
  }
}

export default Client
